"use client";

import { useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { useFinance, useTagPolicy, isExcludedByTag, isIncome, isSpending, isReportable, type TagPolicy, tagPolicyIsDefault } from "@/lib/finance/finance-data-context";
import { currentMonthKey, fmtDate, fmtUsdCents } from "@/lib/finance/format";
import { GROUPS, COLORED_GROUPS, groupOf, groupColor, isIncomeCategory } from "@/lib/finance/categories";
import { Card, PageHeader, ResetFiltersChip, TagBadges, TxnAmount, TxnListItem } from "@/components/finance/ui";
import { TagMenu } from "@/components/finance/TagMenu";
import { CategoryMenu, type CatNode } from "@/components/finance/CategoryMenu";
import { usePageRange, rangeStateFor } from "@/components/finance/RangePicker";
import type { Txn, Account } from "@/lib/finance/types";
import { txnAccountLabel, txnAccountKey } from "@/lib/finance/accountResolve";

const PAGE_SIZE = 100;

// Amount-expression search: when the WHOLE query reads as an amount, the
// search box filters by amount instead of text. Forms: exact ("500",
// "$1,234.56" — magnitude match; "-500" / "+500" pin the sign), comparison
// (">1000", "<=50" — magnitude), range ("100-200", "100..200" — magnitude).
// Cents compare as integers so 10.10 can't miss on float noise.
const AMOUNT_EXPR = /^(>=|<=|>|<)?\s*([+-]?)\$?(\d{1,3}(?:,\d{3})+|\d+)(\.\d{1,2})?$/;
const AMOUNT_RANGE = /^\$?(\d{1,3}(?:,\d{3})+|\d+)(\.\d{1,2})?\s*(?:\.\.|-|–)\s*\$?(\d{1,3}(?:,\d{3})+|\d+)(\.\d{1,2})?$/;
function parseAmountQuery(q: string): ((amount: number) => boolean) | null {
  const cents = (whole: string, frac: string | undefined) =>
    Math.round(Number(`${whole.replace(/,/g, "")}${frac ?? ""}`) * 100);
  let m = AMOUNT_EXPR.exec(q);
  if (m) {
    const [, op, sign, whole, frac] = m;
    const v = cents(whole, frac);
    if (op) {
      return (a) => {
        const c = Math.abs(Math.round(a * 100));
        return op === ">" ? c > v : op === "<" ? c < v : op === ">=" ? c >= v : c <= v;
      };
    }
    if (sign) {
      const signed = sign === "-" ? -v : v;
      return (a) => Math.round(a * 100) === signed;
    }
    return (a) => Math.abs(Math.round(a * 100)) === v;
  }
  m = AMOUNT_RANGE.exec(q);
  if (m) {
    const [, w1, f1, w2, f2] = m;
    const lo = Math.min(cents(w1, f1), cents(w2, f2));
    const hi = Math.max(cents(w1, f1), cents(w2, f2));
    return (a) => {
      const c = Math.abs(Math.round(a * 100));
      return c >= lo && c <= hi;
    };
  }
  return null;
}

export function Transactions() {
  const { txns: rawTxns, accounts: allAccounts } = useFinance();
  const txnsLoading = rawTxns === null;
  const params = useSearchParams();
  const pathname = usePathname();
  const { policy: tagPolicy, reset: resetTagPolicy } = useTagPolicy();
  const [search, setSearch] = useState(params.get("q") ?? "");
  const deferredSearch = useDeferredValue(search);
  const account = params.get("account") ?? "all";
  const [page, setPage] = useState(0);

  // Tag filter (?tags=Business,Reimbursable) — show only rows carrying a
  // selected tag (OR). Independent of the tag budgeting policy (In/Out).
  const tagFilter = useMemo(() => (params.get("tags") ?? "").split(",").filter(Boolean), [params]);
  const tagFilterSet = useMemo(() => new Set(tagFilter), [tagFilter]);

  // The shared page range (persists across the finance pages), so moving
  // Trends → Transactions keeps the same window.
  const dataStart = rawTxns?.length ? rawTxns[rawTxns.length - 1].date.slice(0, 7) : "2010-01";
  const { startMonth, endMonth, setState: setRange, control } = usePageRange(
    dataStart, currentMonthKey(),
  );

  // A drilldown from Trends arrives as ?from=<date>&to=<date> — scope the range
  // to it once on mount, then strip those params (keeping group/q) so a later
  // manual range change isn't re-overridden on re-render.
  const appliedRange = useRef(false);
  useEffect(() => {
    if (appliedRange.current) return;
    appliedRange.current = true;
    const from = params.get("from");
    const to = params.get("to");
    if (from && to) {
      // A drill window that lands exactly on a standard range selects the
      // chip (7/1→7/31 is MTD when today is in July), not Custom.
      setRange(rangeStateFor(from, to, dataStart, currentMonthKey()));
      const next = new URLSearchParams(params.toString());
      next.delete("from");
      next.delete("to");
      const qs = next.toString();
      window.history.replaceState(null, "", qs ? `${pathname}?${qs}` : pathname);
    }
  }, [params, setRange, pathname, dataStart]);

  // Only cash/debit/credit account activity belongs in the transaction ledger.
  // Investment/loan/mortgage-account rows (e.g. the mortgage side of a payment)
  // are balance-tracked, not budgeted — hide them here.
  const budgetableTxns = useMemo(() => (rawTxns ?? []).filter((t) => t.budgetable), [rawTxns]);

  // Account filter options keyed by resolved account (id when linked, else the
  // raw label), so a linked account's masked + bare labels collapse to ONE
  // entry. keyLabel maps the filter key back to its display label.
  const { accountOptions, keyLabel } = useMemo(() => {
    const label = new Map<string, string>();
    for (const t of budgetableTxns) {
      const k = txnAccountKey(t);
      if (!label.has(k)) label.set(k, txnAccountLabel(t, allAccounts));
    }
    const options = [...label.entries()]
      .map(([key, text]) => ({ key, label: text }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return { accountOptions: options, keyLabel: label };
  }, [budgetableTxns, allAccounts]);

  // Three-level category tree for the ledger picker: rollup nature (Income /
  // Expenses / Transfers) at L1; income & transfer categories directly under
  // theirs; spending groups under Expenses, each expanding to its tier-2
  // categories at L3.
  //
  // Leaf identity is HIERARCHICAL: a category that carries rows on both the
  // income side (Income rollup, e.g. tax refunds) and the non-income side
  // (payments) splits into TWO leaves, tokenized "income|Name" / "spend|Name",
  // appearing under Income and under its expense group respectively — so
  // "Income Tax" refunds and payments never mix in one filter. Single-sided
  // categories (the vast majority) keep their bare name as the token, so URLs
  // and saved links look unchanged. Within the non-income side, transfer vs
  // expense placement stays majority-of-rows (stray transfer rows in an
  // expense category don't split it — they match its one leaf).
  const { tree, allCats, expandGroup, resolveToken, tokenOf } = useMemo(() => {
    const incomeSide = new Set<string>();
    const tally = new Map<string, { tr: number; exp: number }>();
    for (const t of budgetableTxns) {
      if (isIncomeCategory(t.category) || t.rollup === "Income") {
        incomeSide.add(t.category);
        continue;
      }
      const e = tally.get(t.category) ?? { tr: 0, exp: 0 };
      if (t.rollup === "Transfer" || t.rollup === "Investment") e.tr++;
      else e.exp++;
      tally.set(t.category, e);
    }
    const dual = new Set([...incomeSide].filter((c) => tally.has(c)));
    const tok = (c: string, side: "income" | "spend") => (dual.has(c) ? `${side}|${c}` : c);
    const transferCats = new Set<string>();
    const expenseByGroup = new Map<string, Set<string>>();
    for (const [cat, e] of tally) {
      if (e.tr > e.exp) transferCats.add(cat);
      else (expenseByGroup.get(groupOf(cat)) ?? expenseByGroup.set(groupOf(cat), new Set()).get(groupOf(cat))!).add(cat);
    }
    const byName = (a: string, b: string) => a.localeCompare(b);
    const leaf = (c: string, side: "income" | "spend"): CatNode => ({ key: `c:${tok(c, side)}`, label: c, cats: [tok(c, side)] });
    const nodes: CatNode[] = [];
    const incomeTokens = [...incomeSide].sort(byName).map((c) => tok(c, "income"));
    if (incomeSide.size)
      nodes.push({ key: "r:Income", label: "Income", cats: incomeTokens, children: [...incomeSide].sort(byName).map((c) => leaf(c, "income")) });
    const expenseGroupNodes = GROUPS.filter((g) => expenseByGroup.has(g)).map((g): CatNode => {
      const cats = [...expenseByGroup.get(g)!].sort(byName);
      return { key: `g:${g}`, label: g, cats: cats.map((c) => tok(c, "spend")), children: cats.map((c) => leaf(c, "spend")) };
    });
    if (expenseGroupNodes.length)
      nodes.push({ key: "r:Expenses", label: "Expenses", cats: expenseGroupNodes.flatMap((n) => n.cats), children: expenseGroupNodes });
    const transferTokens = [...transferCats].sort(byName).map((c) => tok(c, "spend"));
    if (transferCats.size)
      nodes.push({ key: "r:Transfers", label: "Transfers", cats: transferTokens, children: [...transferCats].sort(byName).map((c) => leaf(c, "spend")) });
    const allCats = nodes.flatMap((n) => n.cats);
    const leafSet = new Set(allCats);
    // A row's leaf token: income side by taxonomy or its own rollup, else the
    // category's (single) non-income leaf.
    const tokenOf = (t: Txn) =>
      isIncomeCategory(t.category) || t.rollup === "Income" ? tok(t.category, "income") : tok(t.category, "spend");
    // A URL token → the leaf tokens it names. "income|X" / "spend|X" pick one
    // side; a bare name picks every leaf the category has (back-compat with
    // pre-hierarchy links, where dual categories matched both sides).
    const resolveToken = (s: string): string[] => {
      const bar = s.indexOf("|");
      if (bar >= 0) {
        const side = s.slice(0, bar);
        const name = s.slice(bar + 1);
        if (side === "income" || side === "spend") {
          const t = tok(name, side);
          return leafSet.has(t) ? [t] : [];
        }
        return [];
      }
      if (leafSet.has(s)) return [s];
      return dual.has(s) ? [`income|${s}`, `spend|${s}`] : [];
    };
    // Expand a deep-link group token to its leaf tokens. Group tokens are
    // inherently one-sided: "Income" is the income side, an expense group (or
    // "Expenses"/"Other") the spend side — so a Trends drill that names a
    // group needs no per-category qualification.
    const expandGroup = (g: string): string[] => {
      if (g === "Income") return incomeTokens;
      if (g === "Transfers") return transferTokens;
      if (g === "Expenses") return expenseGroupNodes.flatMap((n) => n.cats);
      if (g === "Other")
        return [...expenseByGroup].filter(([grp]) => !COLORED_GROUPS.includes(grp as never)).flatMap(([, cats]) => [...cats].map((c) => tok(c, "spend")));
      return [...(expenseByGroup.get(g) ?? [])].map((c) => tok(c, "spend"));
    };
    return { tree: nodes, allCats, expandGroup, resolveToken, tokenOf };
  }, [budgetableTxns]);

  // Selected leaf tokens, derived from the URL. Include-style params
  // (?cats=/?groups= from a Trends "view all" deep-link, legacy ?cat/?group)
  // scope to just those; ?excat= subtracts — either from the include set (a
  // Trends "all but a few" link rides on a groups= base) or from everything.
  const catFilter = useMemo(() => {
    if (params.get("catnone")) return new Set<string>(); // explicit "none selected"
    const cats = (params.get("cats") ?? "").split(",").filter(Boolean);
    const groups = (params.get("groups") ?? "").split(",").filter(Boolean);
    const legacyGroup = params.get("group");
    if (legacyGroup && legacyGroup !== "all") groups.push(legacyGroup);
    const legacyCat = params.get("cat");
    if (legacyCat && legacyCat !== "all") cats.push(legacyCat);
    const excluded = new Set((params.get("excat") ?? "").split(",").filter(Boolean).flatMap(resolveToken));
    if (cats.length || groups.length) {
      const sel = new Set<string>();
      for (const c of cats) for (const t of resolveToken(c)) sel.add(t);
      for (const g of groups) for (const t of expandGroup(g)) sel.add(t);
      for (const t of excluded) sel.delete(t);
      return sel;
    }
    if (excluded.size) return new Set(allCats.filter((c) => !excluded.has(c)));
    return new Set(allCats);
  }, [params, allCats, resolveToken, expandGroup]);

  const allCatsSelected = catFilter.size === allCats.length;
  const matchesCategory = useMemo(
    () => (allCatsSelected ? () => true : (t: Txn) => catFilter.has(tokenOf(t))),
    [allCatsSelected, catFilter, tokenOf],
  );

  const filtered = useMemo(() => {
    const q = deferredSearch.trim().toLowerCase();
    const matchesAmount = q ? parseAmountQuery(q) : null;
    // startMonth/endMonth are full-date bounds (YYYY-MM-DD) from useRange.
    return budgetableTxns.filter((t) => {
      if (t.date < startMonth || t.date > endMonth) return false;
      if (!matchesCategory(t)) return false;
      if (account !== "all" && txnAccountKey(t) !== account) return false;
      if (tagFilterSet.size && !t.tags?.some((tag) => tagFilterSet.has(tag))) return false;
      if (matchesAmount) {
        if (!matchesAmount(t.amount)) return false;
      } else if (q) {
        // Search the display description only, NOT t.original — a renamed row
        // should not still be found by its raw bank descriptor.
        const hay = `${t.description} ${t.category} ${keyLabel.get(txnAccountKey(t)) ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    })
      // Deterministic order so same-date rows never flip-flop between loads:
      // newest date first, then largest expense (most-negative amount) first,
      // then description, then sourceId as a final stable tiebreak.
      .sort((a, b) => {
        if (a.date !== b.date) return a.date < b.date ? 1 : -1;
        if (a.amount !== b.amount) return a.amount - b.amount;
        const d = (a.description || "").localeCompare(b.description || "");
        if (d) return d;
        return (a.sourceId || "").localeCompare(b.sourceId || "");
      });
  }, [budgetableTxns, deferredSearch, startMonth, endMonth, matchesCategory, account, tagFilterSet, keyLabel]);

  // Totals use the app-wide reporting predicates: refunds (credits in an
  // expense category) reduce spending rather than counting as income, matching
  // Trends/Cash Flow.
  const totals = useMemo(() => {
    let income = 0;
    let spending = 0;
    for (const t of filtered) {
      if (isIncome(t, tagPolicy)) income += t.amount;
      else if (isSpending(t, tagPolicy)) spending -= t.amount;
    }
    return { income, spending };
  }, [filtered, tagPolicy]);

  const pages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Clamp so a range/filter change that shrinks the result set can't strand
  // the view on an empty page.
  const clampedPage = Math.min(page, pages - 1);
  const pageRows = filtered.slice(clampedPage * PAGE_SIZE, (clampedPage + 1) * PAGE_SIZE);

  // Filter changes update the URL via native history.replaceState — Next syncs
  // useSearchParams from it with NO server round-trip. router.replace re-fetches
  // an RSC payload, which stalls for seconds on a scale-to-zero cold start and
  // made the filter controls feel unresponsive.
  function replaceQuery(mutate: (next: URLSearchParams) => void) {
    setPage(0);
    const next = new URLSearchParams(params.toString());
    mutate(next);
    const qs = next.toString();
    window.history.replaceState(null, "", qs ? `${pathname}?${qs}` : pathname);
  }

  function setParam(key: string, value: string) {
    replaceQuery((next) => {
      if (value === "all" || value === "") next.delete(key);
      else next.set(key, value);
    });
  }

  function setCatFilter(next: Set<string>) {
    const unchecked = allCats.filter((c) => !next.has(c));
    replaceQuery((q) => {
      q.delete("group");
      q.delete("cat");
      q.delete("groups");
      q.delete("cats");
      q.delete("excat");
      q.delete("catnone");
      if (unchecked.length === 0) return; // all selected → no param
      if (next.size === 0) {
        q.set("catnone", "1"); // none selected — an empty ?cats= would re-parse as "all"
        return;
      }
      // Store whichever side is smaller so the URL stays short either way.
      if (next.size <= unchecked.length) q.set("cats", [...next].join(","));
      else q.set("excat", unchecked.join(","));
    });
  }

  function toggleTagFilter(tag: string) {
    replaceQuery((next) => {
      const cur = new Set((next.get("tags") ?? "").split(",").filter(Boolean));
      cur.has(tag) ? cur.delete(tag) : cur.add(tag);
      if (cur.size) next.set("tags", [...cur].join(","));
      else next.delete("tags");
    });
  }
  // Whole-filter replace: the menu's "Only" and "Clear" set it in ONE URL
  // write instead of a toggle per tag (each toggle re-reads the URL, so a
  // burst of them would race).
  function setTagFilter(tags: string[]) {
    replaceQuery((next) => {
      if (tags.length) next.set("tags", tags.join(","));
      else next.delete("tags");
    });
  }

  return (
    <div className="space-y-4">
      <PageHeader title="Transactions">{control}</PageHeader>

      {/* Filter row — scopes everything below it */}
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          placeholder="Search text, or amount: 500, >1000, 100-200…"
          aria-label="Search transactions"
          className="w-full max-w-xs rounded-full border border-edge bg-surface-1 px-4 py-1.5 text-xs placeholder:text-ink-3 focus:border-(--accent) focus:outline-none"
        />
        <CategoryMenu tree={tree} allCats={allCats} selected={catFilter} onChange={setCatFilter} />
        {/* Native select, but hugging its selected label like the pill
            dropdowns beside it: appearance-none drops the native double-arrow
            (replaced by one chevron), and field-sizing:content shrinks the
            control to the current option (capped at max-w-48, then truncated).
            Browsers without field-sizing fall back to the old fixed width. */}
        <div className="relative inline-flex">
          <select
            value={account}
            onChange={(e) => setParam("account", e.target.value)}
            aria-label="Filter by account"
            className="max-w-48 appearance-none truncate rounded-full border border-edge bg-surface-1 py-1.5 pl-3 pr-8 text-xs text-ink-2 [field-sizing:content] hover:bg-surface-3/60"
          >
            <option value="all">All accounts</option>
            {accountOptions.map((a) => (
              <option key={a.key} value={a.key}>
                {a.label}
              </option>
            ))}
          </select>
          <svg
            viewBox="0 0 20 20"
            className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3"
            fill="none"
            stroke="currentColor"
            strokeWidth={1.8}
            aria-hidden
          >
            <path d="M6 8l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>
        <TagMenu filterTags={tagFilter} onToggleFilter={toggleTagFilter} onSetFilter={setTagFilter} />
        {(!allCatsSelected || account !== "all" || tagFilter.length > 0 || search.trim() !== "" || !tagPolicyIsDefault(tagPolicy)) && (
          <ResetFiltersChip
            onReset={() => {
              setSearch("");
              resetTagPolicy();
              replaceQuery((q) => {
                for (const k of ["group", "cat", "groups", "cats", "excat", "catnone", "account", "tags"]) q.delete(k);
              });
            }}
          />
        )}
      </div>

      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 text-xs text-ink-2">
          <span>
            {filtered.length.toLocaleString()} transactions
            {filtered.length > 0 && (
              <>
                {" · "}income <span className="font-medium text-pos">{fmtUsdCents(totals.income)}</span>
                {" · "}spending <span className="font-medium text-neg">{fmtUsdCents(totals.spending)}</span>
                {" · "}total{" "}
                <span className={`font-medium ${totals.income - totals.spending >= 0 ? "text-pos" : "text-neg"}`}>
                  {fmtUsdCents(totals.income - totals.spending)}
                </span>
              </>
            )}
          </span>
          {pages > 1 && <Pager clampedPage={clampedPage} pages={pages} setPage={setPage} />}
        </div>

        {/* Desktop table */}
        <table className="hidden w-full text-sm sm:table">
          <thead className="text-left text-xs text-ink-3">
            <tr>
              <th className="py-1.5 pr-4 font-medium">Date</th>
              <th className="py-1.5 pr-4 font-medium">Description</th>
              <th className="py-1.5 pr-4 font-medium">Category</th>
              <th className="py-1.5 pr-4 font-medium">Account</th>
              <th className="py-1.5 text-right font-medium">Amount</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((t, i) => (
              <TxnRow key={`${t.date}-${i}`} t={t} tagHidden={isExcludedByTag(t, tagPolicy)} policy={tagPolicy} accounts={allAccounts} />
            ))}
          </tbody>
        </table>

        {/* Mobile cards */}
        <ul className="divide-y divide-edge sm:hidden">
          {pageRows.map((t, i) => (
            <TxnListItem
              key={`${t.date}-${i}`}
              t={t}
              muted={!isReportable(t, tagPolicy)}
              badge={<TagBadges tags={t.tags} policy={tagPolicy} />}
            />
          ))}
        </ul>

        {pageRows.length === 0 && (
          <p className="py-6 text-center text-sm text-ink-3">
            {txnsLoading ? "Loading transactions…" : "No matching transactions."}
          </p>
        )}

        {pages > 1 && (
          <div className="mt-3 flex justify-center border-t border-edge pt-3 text-xs text-ink-2">
            <Pager clampedPage={clampedPage} pages={pages} setPage={setPage} />
          </div>
        )}
      </Card>
    </div>
  );
}

/** Prev / "page X of Y" / next — rendered both above and below the ledger. */
function Pager({ clampedPage, pages, setPage }: { clampedPage: number; pages: number; setPage: (p: number) => void }) {
  return (
    <span className="inline-flex items-center gap-2">
      <PageButton disabled={clampedPage === 0} onClick={() => setPage(clampedPage - 1)} label="Previous page">
        ←
      </PageButton>
      page {clampedPage + 1} of {pages.toLocaleString()}
      <PageButton disabled={clampedPage >= pages - 1} onClick={() => setPage(clampedPage + 1)} label="Next page">
        →
      </PageButton>
    </span>
  );
}

function PageButton({
  disabled,
  onClick,
  label,
  children,
}: {
  disabled: boolean;
  onClick: () => void;
  label: string;
  children: string;
}) {
  return (
    <button
      disabled={disabled}
      onClick={onClick}
      aria-label={label}
      className="rounded-full border border-edge px-2.5 py-1 text-xs hover:bg-surface-3 disabled:opacity-40"
    >
      {children}
    </button>
  );
}

function Amount({ t, tagHidden }: { t: Txn; tagHidden?: boolean }) {
  // Budgetable rollup regardless of tags — tag-hiding is conveyed separately.
  const budgetRollup = t.budgetable && (t.rollup === "Expense" || t.rollup === "Income");
  const muted = tagHidden || !budgetRollup;
  return (
    <TxnAmount
      t={t}
      muted={muted}
      title={
        tagHidden
          ? "Hidden from budgeting by a tag"
          : !budgetRollup
            ? "Excluded from budget & cash flow (transfer, investment, or non-spending account)"
            : undefined
      }
    />
  );
}

function TxnRow({ t, tagHidden, policy, accounts }: { t: Txn; tagHidden?: boolean; policy: TagPolicy; accounts: Account[] }) {
  return (
    <tr className="border-t border-edge hover:bg-surface-3/40">
      <td className="whitespace-nowrap py-2 pr-4 text-ink-2">{fmtDate(t.date)}</td>
      <td className="max-w-64 truncate py-2 pr-4 font-medium" title={t.original || t.description}>
        {t.description}
      </td>
      <td className="whitespace-nowrap py-2 pr-4">
        <span className="inline-flex items-center gap-1.5 text-xs text-ink-2">
          <span className="inline-block h-2 w-2 rounded-full" style={{ background: groupColor(groupOf(t.category)) }} />
          {t.category}
          <TagBadges tags={t.tags} policy={policy} />
        </span>
      </td>
      <td className="max-w-44 truncate py-2 pr-4 text-xs text-ink-3">{txnAccountLabel(t, accounts)}</td>
      <td className="py-2 text-right">
        <Amount t={t} tagHidden={tagHidden} />
      </td>
    </tr>
  );
}
